/**
 *
 * ModalChangeBillingCountry
 *
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { DhlText, DhlButton, DhlDropdown } from "@dhl-official/react-library";
import { Variants, Sizes } from "@dhl-official/stencil-library";
import { injectIntl } from "react-intl";
import { trackContentInteraction } from "root/libs/core-libs/src";
import { SEGMENT_TYPES } from "one-time-shipment/src/containers/SpotShipmentPage/constants";
import {
  Icon,
  Headline,
  ContentModal,
  countryAutocompleteFieldMessages,
  Flag,
} from "root/libs/ui-components/src";
import {
  keys,
  countryCodes,
  UTF_EVENT_CONTENT_NAMES,
  UTF_EVENT_TYPES,
  UTF_EVENT_INTERACTIONS,
  UTF_EVENT_CONTENT_POSITION,
} from "root/libs/constants/src";
import checkIcon from "icons/cx/check.svg";
import { breakpoints } from "root/libs/ui-styleguide/src";
import globeIcon from "iconsTwoColors/cx/globe.svg";
import styled from "styled-components";
import { countriesNotAvailableCodes } from "./countriesNotAvailable";

const gap = "2rem";
const xsGap = "1rem";

const Content = styled.div`
  width: 100%;
  height: 100%;

  .margin-s {
    margin-bottom: 2.5rem;
  }
`;

const StyledIcon = styled(Icon)`
  margin: 0 auto;
  margin-bottom: ${gap};
`;

const StyleCheckIcon = styled(Icon)`
  display: inline-flex;
  padding: 0.55rem;
  border-radius: 100%;
  background: var(--dui-colors-icons-positive);
  width: 20px;
  height: 20px;

  & > svg {
    vertical-align: baseline;
  }
`;

const CopyContainer = styled.div`
  text-align: center;
  margin: var(--gaq-size-space-50px) 0 var(--gaq-size-space-50px) 0;

  @media screen and (min-width: ${breakpoints.tablet}) {
    margin: var(--dui-size-space-20x) 0 var(--gaq-size-space-50px) 0;
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    margin: var(--dui-size-space-10x) 0 var(--gaq-size-space-30px) 0;
  }
`;

const CopyText = styled(DhlText)`
  display: block;
  & > *:is(p, span) {
    margin-bottom: ${(props) => (props.noGap === true ? "0px" : gap)};
    margin-top: ${(props) => (props.topGap ? gap : 0)};
    text-align: left;

    margin-bottom: ${(props) => (props.noGap === true ? "0px" : gap)};
  }
`;

const StyledLink = styled(DhlButton)`
  & > .styled:is(button) {
    text-decoration: underline;
    padding: 0 !important;
    line-height: var(--dui-size-line-height-sm) !important;
    height: auto !important;
  }
`;

const AdditionalInformationList = styled.ul`
  list-style-type: none;

  & > li {
    display: flex;
    align-items: center;
    padding-bottom: ${gap};

    p {
      padding-left: ${gap};
    }
  }
`;

const AutocompleteFieldWrapper = styled.div`
  position: relative;
  width: 80%;
  margin: 0 auto;
  text-align: left;
  padding-bottom: ${gap};

  label,
  .has-label {
    padding-left: calc(
      var(--dui-size-space-20x) + var(--dui-size-space-8x)
    ) !important;
  }

  .dhl-validation-feedback-icon.dhl-validation-feedback-icon--only {
    position: absolute;
    top: var(--dui-size-space-8x) !important;
    right: var(--dui-size-space-20x) !important;
  }

  .dropdown-container .content-container:has(+ .show) .input-container input {
    border-color: var(--dui-color-black-500) !important;
    box-shadow: var(--dui-size-space-0) var(--dui-size-space-0)
      var(--dui-size-space-0) var(--dui-size-space-1) var(--dui-color-black-500);
  }

  .options-container.show[data-popper-placement="top"] {
    border-bottom: solid var(--dui-size-space-1) var(--dui-color-gray-400);
    transform: translate3d(0, -4.5rem, 0) !important;
  }

  .options-container.show[data-popper-placement="bottom"] {
    border-top: solid var(--dui-size-space-1) var(--dui-color-gray-400);
  }

  .dropdown-container ul {
    max-height: calc(var(--dui-size-space-24x) * 3) !important;
  }

  ${({ countriesNotAvailableList }) =>
    countriesNotAvailableList?.length &&
    `
    .dropdown-container ul dhl-selection-option:is(${countriesNotAvailableList
      .map((item) => `[data-value="${item}"]`)
      .join()}) {
      pointer-events: none;
      li {
        background: var(--dui-color-gray-100);
        opacity: 0.7;
        text-decoration: line-through;
      }
    }

  `}
`;

const StyledFlag = styled(Flag)`
  position: absolute;
  z-index: 9;
  top: var(--dui-size-space-8x);
  left: var(--dui-size-space-10x);
`;

const StyledFlexContainer = styled.div`
  display: flex;
  text-align: left;
  padding-top: ${xsGap};
  justify-content: center;
`;

const COPY_ID_PREFIX = "ModalChangeBillingCountry";

const ModalChangeBillingCountry = (props) => {
  const {
    intl,
    onClose,
    toggleOriginAndDestination,
    enableImportExport,
    isOpen,
  } = props;
  const countryDropdownRef = useRef(null);
  const countryButtonRef = useRef(null);

  const [country, setCountry] = useState({
    id: "",
    label: "",
    value: "",
  });
  const [listOfCountries, setListOfCountries] = useState([]);
  const [validation, setValidation] = useState(undefined);
  const [filteredCountries, setFilteredCountries] = useState(listOfCountries);
  const [countriesNotAvailableList, setCountriesNotAvailableList] = useState(
    listOfCountries
  );

  // Reset country selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountry({
        id: "",
        label: "",
        value: "",
      });
      setValidation({
        type: "",
      });
    }
  }, [isOpen]);

  function getCopy(id) {
    return intl.formatMessage({ id });
  }

  function onChange(customEvent) {
    const { detail, type } = customEvent;

    if (!detail?.value?.[0]) {
      return;
    }

    const selectedCountry = detail.value[0];

    if (!countryDropdownRef.current) {
      return;
    }

    // Check if the selected country is disabled via DOM
    const selectedItem = countryDropdownRef.current.querySelector(
      `ul dhl-selection-option[data-value="${selectedCountry}"]`
    );
    if (!selectedItem) {
      return;
    }

    const isDisabled = selectedItem.getAttribute("aria-disabled") === "true";

    if (isDisabled) {
      setCountry({
        id: "",
        label: "",
        value: "",
      });

      setValidation({
        type: "",
      });

      return;
    }

    let countryLabel;
    let matchedCountry;
    if (detail && type === "dhlChange") {
      countryLabel = selectedCountry.toLowerCase();
      matchedCountry = listOfCountries.find(
        (countryValue) => countryValue.label.toLowerCase() === countryLabel
      );

      setCountry({
        id: matchedCountry?.id || "",
        label: matchedCountry?.label || "",
        value: matchedCountry?.value || "",
      });
      if (matchedCountry) {
        setValidation({
          type: "valid",
        });
      } else {
        setValidation({
          type: "invalid",
        });
      }
    }
  }

  const handleCountriesNotAvailable = useCallback(() => {
    // Create a filtered list of unavailable country names
    const filtered = listOfCountries
      .filter((countryItem) =>
        countriesNotAvailableCodes.includes(countryItem.id)
      )
      .map((countryItem) => countryItem.value);

    // Update state for the CSS styling
    setCountriesNotAvailableList(filtered);

    // Apply directly to DOM elements using the filtered array instead of state
    // This fixes the timing issue where state might not be updated yet
    if (countryDropdownRef.current) {
      const listItems = countryDropdownRef.current.querySelectorAll("ul li");

      // First, clear all disabled status
      listItems.forEach((listItem) => {
        const newItem = listItem;
        newItem.removeAttribute("aria-disabled");
        newItem.style.textDecoration = "";
        newItem.style.opacity = "";
        newItem.style.backgroundColor = "";
        newItem.style.pointerEvents = "";
      });

      // Then set the disabled status on filtered items
      listItems.forEach((listItem) => {
        const newItem = listItem;
        const itemValue = newItem.getAttribute("data-value");
        if (filtered.includes(itemValue)) {
          newItem.setAttribute("aria-disabled", "true");
          newItem.style.backgroundColor = "var(--dui-color-gray-100)";
          newItem.style.opacity = "0.7";
          newItem.style.pointerEvents = "none";
          newItem.style.textDecoration = "line-through";
        }
      });
    }
  }, [listOfCountries, countriesNotAvailableCodes]);

  const customFilter = (customEvent) => {
    const { type, detail } = customEvent;
    if (detail && type === "dhlFilter") {
      const searchTerm = detail.toLowerCase();
      const filtered = listOfCountries.filter(
        (countryItem) =>
          countryItem.value.toLowerCase().includes(searchTerm) ||
          countryItem.id.toLowerCase().includes(searchTerm) ||
          countryItem.synonyms.toLowerCase().includes(searchTerm)
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(listOfCountries);
    }

    // Apply the disabled status after filtering
    setTimeout(() => {
      handleCountriesNotAvailable();
    }, 100);
  };

  function buildCountryURI() {
    const {
      changeCountryURI,
      changeCountryURIBusiness,
      byTargetChangeCountryURIs,
      byTargetChangeCountryURIsBusiness,
      isBusiness,
    } = props;
    const countryURI = isBusiness ? changeCountryURIBusiness : changeCountryURI;
    const countryID = country.id.toLowerCase();
    const byTargetCountryURI = isBusiness
      ? byTargetChangeCountryURIsBusiness?.[countryID]
      : byTargetChangeCountryURIs?.[countryID];
    return (byTargetCountryURI || countryURI).replace("{country}", countryID);
  }

  function changeCountryHandler() {
    const { isBusiness } = props;
    const { value } = country;
    trackContentInteraction({
      name: UTF_EVENT_CONTENT_NAMES.FORM_SUBMIT,
      type: UTF_EVENT_TYPES.BUTTON,
      interaction: UTF_EVENT_INTERACTIONS.CLICK,
      position: UTF_EVENT_CONTENT_POSITION.CHANGE_BILLING_COUNTRY_MODAL,
      context: isBusiness ? SEGMENT_TYPES.business : SEGMENT_TYPES.private,
      attributes: {
        country: value,
      },
    });
    window.location.href = buildCountryURI();
  }

  function toggleImportShipmentHandler(event) {
    toggleOriginAndDestination(event);
  }

  const handleFocus = () =>
    countryDropdownRef.current.children[0]?.children[1]?.focus();

  const messages = {
    title: getCopy(`${COPY_ID_PREFIX}.title`),
    headline: getCopy(`${COPY_ID_PREFIX}.headline`),
    introBullets: getCopy(`${COPY_ID_PREFIX}.introBullets`),
    bulletOne: intl.formatMessage(
      {
        id: `${COPY_ID_PREFIX}.bulletOne`,
      },
      {
        br: <br key="br" />,
      }
    ),
    bulletTwo: getCopy(`${COPY_ID_PREFIX}.bulletTwo`),
    message: getCopy(`${COPY_ID_PREFIX}.message`),
    countryLabel: getCopy("tradeLaneComponent.countryLabel"),
    countryEmptyOptionLabel: getCopy(
      "tradeLaneComponent.countryEmptyOptionLabel"
    ),
    ctaPrimary: getCopy(`${COPY_ID_PREFIX}.ctaPrimary`),
    ctaSecondary: getCopy(`${COPY_ID_PREFIX}.ctaSecondary`),
    tooltipContent: getCopy(`${COPY_ID_PREFIX}.tooltipContent`),
    tooltipHeading: getCopy(`${COPY_ID_PREFIX}.tooltipHeading`),
    accessibility: getCopy(`${COPY_ID_PREFIX}.accessibility`),
  };

  useEffect(() => {
    const countryList = countryCodes.map((code) => {
      const countryName = intl.formatMessage(
        countryAutocompleteFieldMessages[code]
      );
      const synExists = !!intl.messages[
        countryAutocompleteFieldMessages[code].synonyms
      ];
      let syn = "";
      if (countryAutocompleteFieldMessages[code].synonyms && synExists) {
        syn = getCopy(countryAutocompleteFieldMessages[code].synonyms);
      }
      return {
        id: code,
        label: countryName,
        value: countryName,
        synonyms: syn,
      };
    }, []);
    setListOfCountries(countryList);
    setFilteredCountries(countryList);
  }, []);

  return (
    <ContentModal
      {...props}
      closeButtonAriaLabel={intl.formatMessage({ id: "SS.ContentModal.close" })}
      contentAriaLabel={messages.title}
      onClose={onClose}
      smallCenteredModal
      variant="bottom"
      isScrollable
      style={{ maxWidth: 610 }}
    >
      <Content>
        <CopyContainer>
          <StyledIcon
            fillColor="var(--dui-color-red-500)"
            icon={globeIcon}
            useAs="decorative"
            width={{ mobile: "108px", desktop: "108px" }}
            height={{ mobile: "64px", desktop: "64px" }}
            extraStyles={[{ margin: "0 auto" }]}
          />
          <Headline
            priority={2}
            headlineDisplayType="h"
            weight="700"
            className="margin-s"
            dataTestid="change-country-title"
          >
            {messages.title}
          </Headline>
          <CopyText isParagraph dataTestid="change-country-headline">
            {messages.headline}
          </CopyText>
          <AutocompleteFieldWrapper
            countriesNotAvailableList={countriesNotAvailableList}
          >
            <StyledFlag country={country.id} />
            <DhlDropdown
              ref={countryDropdownRef}
              data={filteredCountries}
              autocomplete="off"
              onDhlChange={onChange}
              id="country-dropdown"
              dataId="country-dropdown"
              variant={{
                type: "static",
                label: messages.countryLabel,
                placeholder: messages.countryEmptyOptionLabel,
              }}
              onDhlOpen={() => {
                // Apply disabled status when dropdown opens
                setTimeout(() => handleCountriesNotAvailable(), 100);
              }}
              validation={validation}
              showFilter
              placeholder={country.value || messages.countryEmptyOptionLabel}
              required
              onDhlFilter={(val) => {
                customFilter(val);
              }}
              onDhlClose={handleFocus}
              onKeyDown={(e) => {
                const { code } = e;
                if (code === keys.TAB) {
                  countryButtonRef.current.focus();
                }
              }}
            />
          </AutocompleteFieldWrapper>
          <DhlButton
            ref={countryButtonRef}
            tabIndex={0}
            name="change-country-cta"
            dataTestid="change-country-cta"
            variant={Variants.primary}
            type="button"
            clickEvent={changeCountryHandler}
            isDisabled={validation?.type !== "valid"}
            onKeyUp={(e) => {
              const { code, shiftKey } = e;
              if (code === keys.TAB && shiftKey) {
                handleFocus();
              }
            }}
          >
            {messages.ctaPrimary}
          </DhlButton>
          <CopyText topGap isParagraph dataTestid="change-country-intro">
            {messages.introBullets}
          </CopyText>
          <AdditionalInformationList>
            <li>
              <div>
                <StyleCheckIcon
                  icon={checkIcon}
                  useAs="decorative"
                  width={{ desktop: "10px", mobile: "10px" }}
                  height={{ desktop: "10px", mobile: "10px" }}
                />
              </div>
              <CopyText
                noGap
                isParagraph
                dataTestid="change-country-bullet-one"
              >
                {messages.bulletOne}
              </CopyText>
            </li>
            <li>
              <div>
                <StyleCheckIcon
                  icon={checkIcon}
                  useAs="decorative"
                  width={{ desktop: "10px", mobile: "10px" }}
                  height={{ desktop: "10px", mobile: "10px" }}
                />
              </div>
              <CopyText
                noGap
                isParagraph
                dataTestid="change-country-bullet-two"
              >
                {messages.bulletTwo}
              </CopyText>
            </li>
          </AdditionalInformationList>
          {enableImportExport && (
            <StyledFlexContainer>
              <CopyText isParagraph>
                <StyledLink
                  key="createImportCta"
                  name="create-import-cta"
                  dataTestid="create-import-cta"
                  variant={Variants.text}
                  size={Sizes.MD}
                  textVariant="dark"
                  type="button"
                  className="create-import-cta-unstyled"
                  clickEvent={toggleImportShipmentHandler}
                  tabIndex={0}
                >
                  {getCopy(`${COPY_ID_PREFIX}.ctaLink`)}
                </StyledLink>
                <span className="visually-hidden">
                  {messages.accessibility}
                </span>
              </CopyText>
            </StyledFlexContainer>
          )}
        </CopyContainer>
      </Content>
    </ContentModal>
  );
};

ModalChangeBillingCountry.propTypes = {
  abTestRunning: PropTypes.bool.isRequired,
  intl: PropTypes.any.isRequired,
  onClose: PropTypes.func,
  changeCountryURI: PropTypes.string,
  changeCountryURIBusiness: PropTypes.string,
  byTargetChangeCountryURIs: PropTypes.object,
  byTargetChangeCountryURIsBusiness: PropTypes.object,
  toggleOriginAndDestination: PropTypes.func.isRequired,
  isBusiness: PropTypes.bool.isRequired,
  enableImportExport: PropTypes.bool,
  isOpen: PropTypes.bool,
};

ModalChangeBillingCountry.defaultProps = {
  onClose: () => {},
  changeCountryURI: "/{country}-en/home/book-online.html",
  changeCountryURIBusiness: "/{country}-en/home/get-a-quote.html",
};

export default injectIntl(ModalChangeBillingCountry);
